import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { SessionResponse } from '@worki/shared';
import * as platform from '@platform';
import { api, RequestError } from '../lib/api.js';
import { apiUrl } from '../lib/config.js';
import { GoogleMark } from './ProviderMarks.js';
import { ErrorNotice } from './ui.js';

/**
 * AUTH-010 — "continue with Google".
 *
 * Web: a link, not a fetch. The visitor leaves for Google's own screen and
 * comes back to `/api/auth/google/callback`, which only a real navigation does.
 *
 * Native (Capacitor): the Google SDK, then `POST /api/auth/google/native` with
 * the ID token and `X-Worki-Client`, no Authorization. Opening `/api/auth/google`
 * in the WebView is refused by Google (`disallowed_useragent`) and would write
 * the session cookie into the system browser, not this app.
 *
 * Hidden when the deployment has no Google credentials, or on desktop until
 * that shell has a plugin. Capacitor is never hidden.
 */

const BUTTON_CLASS =
  'h-11 w-full rounded-xl border border-border bg-card text-sm font-medium text-foreground flex items-center justify-center gap-2.5 hover:bg-muted transition-colors active:scale-[0.98] disabled:opacity-60';

type NativeGoogleHandoff = { pending: true; google: string };

function isSession(data: unknown): data is SessionResponse {
  return Boolean(data && typeof data === 'object' && 'user' in data && 'business' in data);
}

function isHandoff(data: unknown): data is NativeGoogleHandoff {
  return Boolean(
    data &&
      typeof data === 'object' &&
      'pending' in data &&
      (data as { pending: unknown }).pending === true &&
      'google' in data &&
      typeof (data as { google: unknown }).google === 'string',
  );
}

function googleErrorCode(err: unknown): string {
  if (err instanceof RequestError) return err.code;
  const msg = err instanceof Error ? err.message : String(err);
  if (msg.startsWith('errors.')) return msg;
  if (/cancel/i.test(msg)) return 'errors.googleCancelled';
  return 'errors.googleFailed';
}

export function GoogleButton({ returnTo }: { returnTo?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const methods = useQuery({
    queryKey: ['auth-methods'],
    queryFn: () => api.get<{ google: boolean }>('/auth/methods'),
    staleTime: Infinity,
    retry: false,
  });

  if (platform.googleAuth === 'none') return null;
  if (!methods.data?.google) return null;

  const href = apiUrl(`/auth/google${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`);

  const onNative = async () => {
    setBusy(true);
    setError('');
    try {
      const { idToken } = await platform.googleSignIn();
      const data = await api.post<SessionResponse | NativeGoogleHandoff>(
        '/auth/google/native',
        { idToken },
        { anonymous: true },
      );
      if (isHandoff(data)) {
        navigate(`/signup?google=${encodeURIComponent(data.google)}`, { replace: true });
        return;
      }
      if (isSession(data)) {
        queryClient.setQueryData(['session'], data);
        navigate(returnTo ?? '/', { replace: true });
        return;
      }
      throw new Error('errors.googleFailed');
    } catch (err) {
      setError(googleErrorCode(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {error && <ErrorNotice code={error} />}

      {platform.googleAuth === 'plugin' ? (
        <button type="button" className={BUTTON_CLASS} disabled={busy} onClick={() => void onNative()}>
          <GoogleMark />
          {t('auth.google')}
        </button>
      ) : (
        <a href={href} className={BUTTON_CLASS}>
          <GoogleMark />
          {t('auth.google')}
        </a>
      )}

      <div className="flex items-center gap-3" aria-hidden>
        <span className="h-px flex-1 bg-border" />
        <span className="text-[11px] text-muted-foreground">{t('auth.orEmail')}</span>
        <span className="h-px flex-1 bg-border" />
      </div>
    </>
  );
}
